import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toZonedTime } from 'date-fns-tz';
import { timeZoneMapping } from './enum';

interface TimeZoneContextProps {
  timeZone: string;
}

const TimeZoneContext = createContext<TimeZoneContextProps | undefined>(
  undefined,
);

interface TimeZoneProviderProps {
  settings: Record<string, any>;
  dataUpdatedAt: number | null;
  children: ReactNode;
}

/**
 * Resolves a timezone string to a valid IANA timezone.
 * Handles legacy formats from migrated users (e.g. "GMT+1", "CEST", "W. Europe Standard Time")
 * by mapping known abbreviations and validating with date-fns-tz.
 */
const resolveTimeZone = (tz: string): string => {
  // Check if it's a known legacy abbreviation
  const mapped = timeZoneMapping[tz as keyof typeof timeZoneMapping];
  if (mapped) {
    return mapped;
  }

  // Validate by attempting a conversion
  try {
    const result = toZonedTime(new Date(), tz);
    if (!isNaN(result.getTime())) {
      return tz;
    }
  } catch {
    // Invalid timezone
  }

  return 'UTC';
};

export const TimeZoneProvider = ({
  settings,
  dataUpdatedAt,
  children,
}: TimeZoneProviderProps) => {
  const [timeZone, setTimeZone] = useState<string>('UTC');

  useEffect(() => {
    if (settings) {
      const rawTimeZone =
        settings?.['ribbonPlugin.settings']?.time_zone || 'UTC';
      const newTimeZone = resolveTimeZone(rawTimeZone);
      if (newTimeZone !== timeZone) {
        setTimeZone(newTimeZone);
      }
    }
  }, [dataUpdatedAt, settings, timeZone]);

  const value = useMemo(
    () => ({
      timeZone,
    }),
    [timeZone],
  );

  return (
    <TimeZoneContext.Provider value={value}>
      {children}
    </TimeZoneContext.Provider>
  );
};

export const useTimeZoneContext = () => {
  const context = useContext(TimeZoneContext);
  if (context === undefined) {
    throw new Error(
      'useTimeZoneContext must be used within a TimeZoneProvider',
    );
  }
  return context;
};
